import type { adminCore as ru } from '../ru/adminCore';

export const adminCore: typeof ru = {
  permissions: {
    noOptions: 'No options',
    loadFailed: 'Failed to load permissions.',
    enterCode: 'Enter a permission code.',
    permissionSaved: (code: string) => `Permission ${code} saved.`,
    saveFailed: 'Failed to save permission.',
    selectAccount: 'Select an account.',
    accountUpdated: 'Account permissions updated.',
    accountUpdateFailed: 'Failed to update account permissions.',
    title: 'Roles & permissions',
    newRole: '+ New role',
    permissionCode: 'Permission code',
    description: 'Description',
    descriptionPlaceholder: 'Permission purpose, visible to admins',
    saving: 'Saving…',
    failurePrefix: 'Failed to',
    searchPlaceholder: 'Name or uid...',
    filterRole: 'Role',
    filterPermission: 'Permission',
    onlyWithRoles: 'With roles only',
    withoutRoles: 'No roles',
    find: 'Search',
    accounts: 'Accounts',
    refresh: 'Refresh',
    accountsNotFound: 'No accounts found.',
    noName: 'No name',
    you: 'you',
    assignShort: '+ Assign',
    pagination: (page: number, totalPages: number, totalElements: number) =>
      `Page ${page} / ${totalPages} · ${totalElements} acc.`,
    next: 'Next',
    assignTitle: (name: string) => `Assign to: ${name}`,
    savingShort: '…',
    rolesAndPermissionsEmpty: 'No roles or permissions created yet.',
    roles: 'Roles',
    permissions: 'Permissions',
    noDescription: 'No description',
    rolesCatalog: 'Roles catalog',
    activeRoles: (count: number) => `${count} active ${count === 1 ? 'role' : 'roles'}`,
    roleFormTodoToast: 'TODO Phase 2: role creation form',
    addRoleShort: '+ role',
    noRoles: 'No roles created.',
    accountsShort: (count: number) => `${count} acc.`,
    permissionsCatalog: 'Permissions catalog',
    activePermissions: (count: number) => `${count} active`,
    addPermissionShort: '+ perm',
    noPermissions: 'No permissions created.'
  },
  tags: {
    newTagName: 'New tag',
    loadFailed: 'Failed to load tags.',
    fillCodeAndName: 'Fill in code and name.',
    saving: 'Saving tag...',
    saved: 'Tag saved.',
    saveFailed: 'Failed to save tag.',
    deleting: 'Deleting tag...',
    deleted: 'Tag deleted.',
    deleteFailed: 'Failed to delete tag.',
    title: 'Player tags',
    createSubmit: 'Create tag',
    searchPlaceholder: 'Search by name or code',
    searchAria: 'Search tags',
    notFound: 'Nothing found.',
    editAria: (name: string) => `Edit tag ${name}`,
    deleteAria: (name: string) => `Delete tag ${name}`,
    newTag: '+ New tag',
    empty: 'The tag registry is empty.',
    edit: 'Edit',
    expandAria: (name: string) => `Show players with tag ${name}`,
    collapseAria: (name: string) => `Hide players with tag ${name}`,
    players: {
      title: 'Players with tag',
      empty: 'No one has this tag',
      loadFailed: 'Failed to load players.',
      loadMore: 'Show more',
      removeTag: 'Remove tag',
      removeTagAria: (name: string) => `Remove tag from player ${name}`,
      removed: 'Tag removed.',
      removeFailed: 'Failed to remove tag.',
      unknownNick: 'No nickname'
    },
    noTag: 'No tag',
    noColor: 'No color',
    deleteModal: {
      kicker: 'ACTION',
      title: 'Confirm deletion',
      confirmPrefix: 'Delete tag',
      confirmSuffix: '? This action cannot be undone.',
      confirmFallback: 'Delete the selected tag? This action cannot be undone.',
      submit: 'Delete tag'
    },
    editor: {
      kicker: 'PLAYER TAG',
      createTitle: 'Create tag',
      editTitle: 'Edit tag',
      namePlaceholder: 'Administrator',
      codeLabel: 'Code',
      nameLabel: 'Name',
      iconLabel: 'Icon',
      colorTitle: 'Color',
      noColorHint: 'A tag without a color is shown in the default color in game.',
      legacyColorNote: 'The current color is stored in a legacy format. If you change the color, it will be replaced.',
      iconUpload: 'Upload image',
      iconUploading: 'Uploading...',
      iconClear: 'Clear icon',
      iconPlaceholder: 'Text alias or URL',
      iconHint: 'PNG is recommended for the game: Panorama reliably loads png/jpg over http.',
      iconUploadFailed: 'Failed to upload the image.',
      preview: 'Preview',
      gamePreview: 'In game',
      gamePreviewHint: 'Player card in the PvP panel, 1:1 at 1080p.',
      previewPlayerName: 'Player',
      previewRank: (value: number) => `Rank: ${value}`,
      previewMatches: (value: number) => `Matches: ${value}`,
      previewAliasIcon: (alias: string) => `In-game icon: ${alias}`,
      saveChanges: 'Save changes'
    }
  },
  catalogs: {
    tabs: {
      heroes: 'NPC heroes',
      abilities: 'NPC abilities',
      items: 'NPC items'
    },
    title: 'Catalogs',
    warningPill: 'Changes affect statistics calculation',
    footerBanner: 'Editing a catalog affects current matches and the recalculation of public tops.',
    resources: {
      heroes: {
        entityLabel: 'hero',
        entryNoun: 'heroes',
        kicker: 'Heroes catalog',
        title: 'Available NPC heroes'
      },
      abilities: {
        entityLabel: 'ability',
        entryNoun: 'abilities',
        kicker: 'Abilities catalog',
        title: 'Available NPC abilities'
      },
      items: {
        entryNoun: 'items',
        kicker: 'Items catalog',
        title: 'Available NPC items'
      }
    },
    table: {
      displayName: 'Display name',
      nameCode: 'Name (code)',
      image: 'Image (URL / upload)',
      preview: 'Preview',
      searchByCodeOrName: 'Search by code or name',
      searchByName: 'Search by name',
      deleteEntry: 'Delete entry',
      deleteAria: (item: string) => `Delete ${item}`,
      bulkHide: 'Hide bulk import',
      bulkShow: 'Bulk import',
      bulkPlaceholder: 'One entry per line.\nReplaces the current list after you click "Replace".',
      bulkReplace: 'Replace',
      copyCurrent: 'Copy current'
    },
    row: {
      resetDisplayTitle: 'Reset displayName',
      resetDisplayAria: 'Reset display name',
      imagePlaceholder: 'Image URL or /files/catalog/…',
      resetImageTitle: 'Reset imageUrl',
      resetImageAria: 'Reset image URL',
      upload: 'Upload image',
      uploadingAria: 'Loading...',
      noPreview: 'none',
      renameTitle: 'Rename code',
      renameAria: (name: string) => `Rename code ${name}`,
      deleteRow: 'Delete row'
    },
    loadFailed: (kicker: string) => `Failed to load ${kicker.toLowerCase()}.`,
    renamed: (oldName: string, newName: string) => `Code renamed: ${oldName} → ${newName}`,
    renameFailed: 'Failed to rename entry.',
    fileTooLarge: 'File is too large (max 5 MB).',
    imageUploaded: 'Image uploaded. Save the catalog to apply it.',
    imageUploadFailed: 'Failed to upload image.',
    saved: (kicker: string) => `${kicker} saved.`,
    saveFailed: (kicker: string) => `Failed to save ${kicker.toLowerCase()}.`,
    refresh: 'Refresh',
    revert: 'Revert',
    noChanges: 'No changes',
    saving: 'Saving…',
    loading: 'Loading catalog…',
    nothingFound: 'Nothing found for this query.',
    emptyWithHint: 'The catalog is empty. Add the first entry.',
    empty: 'The catalog is empty.',
    addEntry: '+ Add entry',
    addEntryPlaceholder: 'Add entry',
    emptyRowsNotSaved: 'Rows with an empty code are not saved.',
    totalEntries: 'Total entries',
    abilitiesListTitle: 'List of available NPC abilities',
    failurePrefix: 'failed to',
    rename: {
      kicker: 'Catalog',
      title: (entityLabel: string) => `Rename ${entityLabel} code`,
      hintPrefix: 'This operation',
      hintStrong: 'atomically rewrites history',
      hintSuffix:
        ': the code changes in the catalog, matches, bans and presets. Statistics are kept under the new code. This action cannot be undone.',
      currentCode: 'Current code',
      newCode: 'New code',
      sameCode: 'The new code matches the current one.',
      saving: 'Renaming…',
      submit: 'Rename'
    }
  }
};
